import type { ModuleStoreState } from "@/store/moduleStore";
import { moduleStore } from "@/store/moduleStore";
import { isIModule } from "@/ts/guards";
import type { ModuleNameToType } from "@/ts/module";

const makeSelector = (name: string) => (state: ModuleStoreState) =>
	state.selectors.moduleByName(name);

/**
 * Get modules from the store. If they already exist, return them synchronously.
 * If not, subscribe to the store and return them asynchronously.
 */
export const getModules = async <K extends Array<keyof ModuleNameToType>>(
	moduleNames: [...K],
): Promise<{ [I in keyof K]: ModuleNameToType[K[I]] }> => {
	const { selectors } = moduleStore.getState();
	const syncMods = moduleNames.map((name) => selectors.moduleByName(name));
	if (syncMods.every(isIModule)) {
		return syncMods as { [I in keyof K]: ModuleNameToType[K[I]] };
	}

	return Promise.all(
		moduleNames.map(
			(name) =>
				new Promise((resolve) => {
					const unsub = moduleStore.subscribe(
						makeSelector(name as string),
						(mod) => {
							if (isIModule(mod)) {
								unsub();
								resolve(mod);
							}
						},
					);
				}),
		),
	) as Promise<{ [I in keyof K]: ModuleNameToType[K[I]] }>;
};
